<script setup lang="ts">
/*
* Компонент SuggestionCard - карточка отображения предложения по улучшению
*
* Основные функции:
* - Отображение полной информации о предложении (заголовок, описание, автор, дата)
* - Управление статусом предложения через визуальные индикаторы
* - Взаимодействие с предложением: ответ, лайки/дизлайки
* - Просмотр профиля автора предложения
* - Отображение ответа на предложение если он есть
*
* Особенности:
* - Поддержка анонимных предложений
* - Интеграция с системой голосования (лайки/дизлайки)
* - Адаптивный дизайн для мобильных устройств
*/

// Импорты Vue и Vuetify
import { computed, ref } from 'vue'
import { useDisplay } from 'vuetify/framework'

// Импорты хранилищ
import { useImprovementsStore } from '@/modules/improvements/stores/improvementsStore'

// Импорты утилит
import {
  getPpuAggregatedStatusColor,
  getPpuAggregatedStatusLabel,
  getPpuStatusColor,
  getPpuStatusLabel,
} from '@/modules/improvements/utils/ppuStatusHelper'
import { formatDateOnly, getFullName } from '@/utils/formatters'
import { getRandomAvatar } from '@/utils/getRandomAvatar'


// Импорты компонентов
import CustomDivider from '@/components/createdUi/CustomDivider.vue'
import EmployeeProfileSidebar from '@/components/createdUi/EmployeeProfileSidebar.vue'

// Импорты типов
import type { ISuggestionCardProps } from '@/modules/improvements/types/ISuggestionCardProps'

const props = defineProps<ISuggestionCardProps>()

// В шаблоне два корня (карточка и сайдбар профиля) — атрибуты родителя, например якорь онбординга, отдаём карточке
defineOptions({ inheritAttrs: false })

const emit = defineEmits<{
  (e: 'clickCard'): void
}>()

// Инициализация хранилищ
const improvementsStore = useImprovementsStore()


// Инициализация утилит отображения Vuetify
const { xs } = useDisplay()

// Реактивные состояния компонента
// Видимость модалки профиля автора
const isCreatorModalOpen = ref(false)

// ID выбранного сотрудника для просмотра профиля
const selectedEmployeeId = ref('')

// Вычисляемые свойства
// Аватар автора предложения или сгенерированный аватар
const getAvatar = computed(() => {
  if (props.createdBy && props.createdBy.avatar) {
    return props.createdBy.avatar
  }
  return getRandomAvatar()
})

const answerSectionTitle = computed(() => {
  if (
    props.status === 'returned_to_author'
    || props.status === 'on_revision'
    || props.status === 'pending_department_head'
    || props.aggregatedStatus === 'under_revision'
  ) {
    return 'Комментарий к доработке'
  }
  return 'Решение'
})

const aggregatedStatusLabel = computed(() =>
  getPpuAggregatedStatusLabel(props.aggregatedStatus, props.aggregatedStatusDisplay),
)

const aggregatedStatusColor = computed(() => getPpuAggregatedStatusColor(props.aggregatedStatus))

const stageStatusLabel = computed(() =>
  getPpuStatusLabel(props.status, props.statusDisplay),
)

const stageStatusColor = computed(() => getPpuStatusColor(props.status))



/**
 * Показывает модальное окно с профилем автора предложения
 *
 * Назначение:
 * - Открывает боковую панель с детальной информацией об авторе
 * - Используется при клике на аватар или имя автора
 *
 * Входные данные:
 * - id: string | undefined - идентификатор сотрудника
 */
function showCreatorModal(id: string | undefined) {
  if (!id) return
  selectedEmployeeId.value = id
  isCreatorModalOpen.value = true
}

const hasRequiresAction = computed(() => Boolean(props.requiresAction || props.requires_action))
</script>

<template>
  <VCard
    v-bind="$attrs"
    class="suggestion-card d-flex flex-column gap-4 pa-4 cursor-pointer"
    :class="{ 'card--requires-action': hasRequiresAction }"
    @click="emit('clickCard')"
  >
    <!-- Первая строка: статус и заголовок -->

    <div class="d-flex flex-column min-w-0">
      <div class="d-flex align-center ga-2 flex-wrap">
        <VChip
          v-if="aggregatedStatus"
          size="small"
          variant="tonal"
          :color="aggregatedStatusColor"
        >
          {{ aggregatedStatusLabel }}
        </VChip>
        <span
          class="body-2 font-weight-medium"
          :class="`text-${stageStatusColor}`"
        >
          {{ stageStatusLabel }}
        </span>
      </div>
      <span class="text-h5 mt-5">{{ title }}</span>
    </div>

    <!-- Вторая строка: текст предложения -->
    <div>
      <p class="body-1 suggestion-card__text">
        {{ text }}
      </p>
    </div>

    <!-- Третья строка: автор -->
    <div v-if="props.createdBy">
      <div class="d-inline-flex align-center gap-2 cursor-pointer" @click.stop="showCreatorModal(createdBy?.id)">
        <VAvatar :image="getAvatar" size="32" />
        <span class="body-1-500 suggestion-card__count">
          {{ createdBy ? getFullName(createdBy) : '' }}
        </span>
      </div>
    </div>

    <!-- Четвертая строка: мета-информация и действия -->
    <div>
      <div class="d-flex justify-space-between w-100 suggestion-card__info" :class="{ 'flex-column': xs, 'align-center': !xs }">
        <!-- Левая часть: создатель, дата и отдел -->
        <div class="d-flex gap-4" :class="{ 'flex-column': xs, 'align-center': !xs }">
          <div v-if="!props.createdBy" class="d-flex align-center gap-1">
            <VIcon icon="ri-user-line" size="20" />
            <span>Анонимно</span>
          </div>

          <div class="d-flex align-center gap-1">
            <VIcon icon="ri-calendar-line" size="20" />
            <span>{{ formatDateOnly(createdAt) }}</span>
          </div>

          <div v-if="props.createdBy" class="d-flex align-center gap-1">
            <VIcon icon="ri-building-4-line" size="20" />
            <span>{{ createdBy?.department_name || 'Не указано' }}</span>
          </div>
        </div>

        <!-- Правая часть: лайки и дизлайки -->
        <div class="d-flex gap-1" :class="{ 'mt-2': xs }" @click.stop>
          <div class="d-flex align-center">
            <VIconBtn
              :loading="improvementsStore.likeInProgress"
              class="btn--clean"
              icon="ri-thumb-up-line"
              variant="plain"
              color="success"
              @click.stop="improvementsStore.likeSuggestion(id)"
            />
            <span class="body-1-500 suggestion-card__count">{{ likesCount || 0 }}</span>
          </div>
          <div class="d-flex align-center">
            <VIconBtn
              :loading="improvementsStore.likeInProgress"
              class="btn--clean"
              variant="plain"
              color="error"
              icon="ri-thumb-down-line"
              @click.stop="improvementsStore.dislikeSuggestion(id)"
            />
            <span class="body-1-500 suggestion-card__count">{{ dislikesCount || 0 }}</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Пятая строка: ответ (если есть) -->
    <div v-if="props.answer" class="d-flex flex-column gap-2">
      <CustomDivider :title="answerSectionTitle" />

      <p class="body-1 suggestion-card__text">
        {{ answer }}
      </p>
    </div>
  </VCard>


  <EmployeeProfileSidebar
    v-model="isCreatorModalOpen"
    :employee-id="selectedEmployeeId"
  />
</template>

<style scoped lang="scss">
.suggestion-card {
  &.card--requires-action {
    border-bottom: 4px solid rgb(var(--v-theme-error)) !important;
  }

  &__text,
  &__info {
    color: rgb(var(--v-theme-text-secondary), var(--v-medium-emphasis-opacity));
  }

  &__count {
  color: rgb(var(--v-theme-text-primary), var(--v-high-emphasis-opacity));
  }
}
</style>
